/**
 * 표기 포맷터. **값이 아니라 표기만 바꾼다** (`docs/22` §6).
 *
 * 환율 변환도 다통화도 없다 — `price` 는 원 단위 정수다(규약, 부동소수점 금지).
 * 영어 화면은 `39,000원` 을 `KRW 39,000` 으로 적을 뿐이다.
 */
/** 방송 편성의 기준 시각. **언어가 바뀌어도 이것은 바뀌지 않는다** — 아래 주석 */
const SCHEDULE_TIME_ZONE = 'Asia/Seoul';
const THOUSANDS = /\B(?=(\d{3})+(?!\d))/g;
/**
 * 금액 표시.
 *
 * **`toLocaleString` 을 쓰지 않는다.** `docs/22` §6.1 의 「구현」 칸은 그것을 지목하지만,
 * `bloom-app/src/format.ts` 가 정확히 그 반대를 결정해 두었다 — "결과가 런타임 ICU 구성에
 * 좌우되면 같은 코드가 기기마다 다른 문구를 만든다"(T4 payload 빌더에서 온 판단이고,
 * 거기서는 그것이 곧 **발송된 알림의 문구**였다). 두 문서 중 뒤엣것이 이미 코드로 서 있고
 * 근거도 구체적이라 그쪽을 따랐다.
 *
 * **잃는 것은 없다.** `docs/22` 표가 요구하는 것은 결과 표기(`39,000원` · `KRW 39,000`)이고
 * 자릿수 구분을 직접 하면 그 표기가 **런타임과 무관하게** 나온다.
 *
 * 소수가 들어오면 버린다 — 반올림은 표시가 정할 일이 아니다.
 */
export function formatPrice(price, locale) {
    const sign = price < 0 ? '-' : '';
    const grouped = String(Math.trunc(Math.abs(price))).replace(THOUSANDS, ',');
    return locale === 'ko' ? `${sign}${grouped}원` : `KRW ${sign}${grouped}`;
}
/**
 * 한국어 표기용 부품. `en-US` 로 뽑는 이유는 **숫자 부품을 라틴 숫자로 고정**하기 위함이다 —
 * 로케일에 따라 아라비아-인도 숫자가 나오는 경우가 있고, 조립은 우리가 한다.
 * `hourCycle: 'h23'` 은 자정이 `24:00` 으로 나오는 ICU 구성을 막는다.
 */
const KO_PARTS = new Intl.DateTimeFormat('en-US', {
    timeZone: SCHEDULE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});
/** 영어 표기는 월 이름이 필요해 `format()` 을 그대로 쓴다 — `Aug 7, 2026, 14:30`. */
const EN_FORMAT = new Intl.DateTimeFormat('en-US', {
    timeZone: SCHEDULE_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});
function partsOf(formatter, date) {
    const out = {};
    for (const part of formatter.formatToParts(date))
        out[part.type] = part.value;
    return out;
}
/**
 * 서버는 UTC ISO 8601 로 준다. **표시 시점에만** KST 로 바꾼다 (규약).
 *
 * **언어가 바뀌어도 타임존은 KST 그대로다.** 브라우저 로컬 시간대에 맡기지 않는 이유가
 * `bloom-console/src/format.ts` 에 이미 적혀 있다 — "관리자가 다른 시간대에 있어도 방송
 * 편성의 기준 시각은 KST 하나여야 한다". 바뀌는 것은 표기뿐이다.
 *
 * **영어 표기에는 `KST` 를 눈에 보이게 붙인다.** 붙이지 않으면 해외 관람자가 자기 지역
 * 시각으로 읽고 편성 시각을 잘못 이해한다 (`docs/22` §6.2).
 *
 * 파싱할 수 없는 값은 **그대로 돌려준다** — 표시 함수가 오류를 만들지 않는다.
 */
export function formatDateTimeKst(iso, locale) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    if (locale === 'en')
        return `${EN_FORMAT.format(date)} KST`;
    const p = partsOf(KO_PARTS, date);
    return `${p['year'] ?? ''}.${p['month'] ?? ''}.${p['day'] ?? ''} ${p['hour'] ?? ''}:${p['minute'] ?? ''}`;
}
/**
 * `m:ss`. **로케일을 받지 않는다** — 숫자와 콜론뿐이라 사전을 거칠 것이 없고, 그래서
 * 영어 화면의 한글 훑기(`docs/22` §4.1-b)와도 무관하다.
 *
 * **시 자리가 없다.** 3600초는 `60:00` 이다. 상품 영상은 30~120초라 지금 문제가 아니고,
 * 자리를 더하면 아래 `formatDuration` 의 고정된 출력이 함께 바뀐다 — 콘솔의 영상 메타
 * 표기가 그것에 매여 있으므로 필요해질 때 **둘을 함께** 정한다.
 */
export function formatClock(seconds) {
    const total = Math.max(0, Math.trunc(seconds));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
/** `durationSec` 은 ffprobe 가 올림해 넣은 정수다 (`docs/04`). */
export function formatDuration(seconds, locale) {
    const total = Math.max(0, Math.trunc(seconds));
    const clock = formatClock(total);
    return locale === 'ko' ? `${clock} (${total}초)` : `${clock} (${total}s)`;
}
/**
 * 하루 안의 시각 `HH:MM`. **기기의 시간대**로 쓴다 (v27, `docs/55` §9 · 절대 규칙 73).
 *
 * ── ★ `formatDateTimeKst` 를 쓰지 않는다 ──────────────────────
 *
 * 저쪽은 **「운영자에게 보이는 편성 시각은 KST 하나」**라는 결정이고(위 `SCHEDULE_TIME_ZONE`),
 * 이쪽은 **시청자의 「지금」**이다. 편성표의 「지금」 선은 **보는 사람의 지금**이라,
 * 암스테르담에서 그 선에 `21:30` 이 적히면 그 화면이 말하는 모든 것이 거짓이 된다 —
 * 무대가 넷인데(인도·미국·태국·암스테르담) **셋에서만 틀리고 서울에서는 완벽해 보인다.**
 *
 * ── ★★ 24시간 표기를 두 언어에 다 쓴다 ──────────────────────
 *
 * `2:25 PM` 은 5분 눈금에서 **라벨끼리 부딪힌다.** 이 문자열은 문장이 아니라 **자의
 * 눈금**이고, 그래서 로케일을 받으면서도 결과가 같다 — 받는 이유는 **호출부가 다른
 * 포맷터와 같은 모양이게** 하려는 것뿐이다(언젠가 갈릴 자리라는 표시이기도 하다).
 *
 * **`toLocaleString` 을 쓰지 않는 것은 위 `formatPrice` 와 같은 이유다** — 결과가 런타임
 * ICU 구성에 좌우되면 같은 코드가 기기마다 다른 문구를 만든다. 여기서는 `Date` 의
 * 지역 시각 게터로 직접 만든다.
 *
 * 숫자와 콜론뿐이라 **한글 훑기와 무관하다**(`formatClock` 이 로케일을 안 받는 그 이유).
 */
export function formatTimeOfDay(epochMs, _locale) {
    const date = new Date(epochMs);
    if (Number.isNaN(date.getTime()))
        return '';
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
}
//# sourceMappingURL=format.js.map